import inputVariables from "@krystal/css/components/input?inline";
import { LitElement, html, isServer, nothing, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { when } from "lit/directives/when.js";
import SlotController from "@/common/controllers/slot";
import { cn } from "@/utilities/css";
import EkoElement from "@/utilities/element";
import "@/components/tooltip";
import "@/components/label";
import { createSlotChangeHandler, setInitialSlotProperties } from "@/utilities/slot";
import style from "./select.css?inline";

type SlottedElement = "eko-avatar" | "eko-flag" | "eko-icon" | "eko-dot" | null;

@customElement("eko-select")
export class Select extends EkoElement {
  static formAssociated = true;
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

  static slotPropertyMap = {
    after: "afterElement",
    before: "beforeElement",
    prefix: "prefixElement",
    suffix: "suffixElement",
  } as const;

  static styles = [...EkoElement.styles, unsafeCSS(inputVariables), unsafeCSS(style)];

  private _initialValue: string | null = null;

  private afterSlot = new SlotController(this, "after");

  private handleBlur = () => {
    this.dispatchEvent(new FocusEvent("blur", { bubbles: true }));
    this.checkValidity();
  };

  private handleChange = () => {
    this.updateFormValue();
    this.checkValidity();

    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { value: this.value },
      })
    );
  };

  private handleFocus = () => {
    this.dispatchEvent(new FocusEvent("focus", { bubbles: true }));
  };

  private handleOptionsSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;

    this.updateOptionsFromSlot(slot);
  };

  private handleSlotChange = createSlotChangeHandler(this, Select.slotPropertyMap);

  private internals: ElementInternals;

  @query("select")
  private nativeSelect?: HTMLSelectElement;

  @query("slot#eko-select__options")
  private optionsSlot?: HTMLSlotElement;

  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @state() afterElement: SlottedElement = null;
  @state() beforeElement: SlottedElement = null;
  @property() class: string = "";
  @property() description: string = "";
  @property({ reflect: true, type: Boolean }) disabled: boolean = false;
  @property() error: string = "";
  @property() help: string = "";
  @property() id: string = `select-${Math.random().toString(36).substr(2, 9)}`;
  @property() label: string = "";
  @property() name: string = "";
  @state() options: Element[] = [];
  @property() placeholder: string = "";
  @state() prefixElement: SlottedElement = null;
  @property({ reflect: true, type: Boolean }) readonly: boolean = false;
  @property({ type: Boolean }) required: boolean = false;
  @state() suffixElement: SlottedElement = null;

  constructor() {
    super();

    this.internals = this.attachInternals();
  }

  private collectOptions(elements: Element[]): Element[] {
    return elements.flatMap((element) => {
      return element.tagName === "OPTGROUP"
        ? Array.from(element.querySelectorAll("option"))
        : element;
    });
  }

  private computeSelectedValue(elements: Element[]): string {
    const options = this.collectOptions(elements);

    const selectedEnabled = options.filter(
      (option) => option.hasAttribute("selected") && !this.isOptionDisabled(option)
    );

    if (selectedEnabled.length > 0) {
      return selectedEnabled[selectedEnabled.length - 1].getAttribute("value") ?? "";
    }

    // Respect disabled+selected placeholders (e.g. <option disabled selected>)
    const anySelected = [...options].reverse().find((option) => option.hasAttribute("selected"));

    if (anySelected) {
      return anySelected.getAttribute("value") ?? "";
    }

    // When a placeholder is provided, default to it (value "")
    if (this.placeholder) return "";

    // No selected attribute at all — default to first enabled option
    const firstEnabled = options.find((option) => !this.isOptionDisabled(option));

    return firstEnabled?.getAttribute("value") ?? "";
  }

  private isOptionDisabled(option: Element): boolean {
    if (option.hasAttribute("disabled")) return true;

    const parent = option.parentElement;

    return parent?.tagName === "OPTGROUP" && parent.hasAttribute("disabled");
  }

  private updateFormValue() {
    this.internals.setFormValue(this.value);
  }

  private updateOptionsFromSlot(slot: HTMLSlotElement) {
    const assignedNodes = slot.assignedNodes();

    this.options = assignedNodes
      .filter((node) => node.nodeType === 1)
      .map((node) => node as Element);
  }

  checkValidity(): boolean {
    const select = this.nativeSelect;

    if (!select) return true;

    const isNativeValid = select.checkValidity();

    if (!isNativeValid) {
      this.internals.setValidity(select.validity, select.validationMessage, select);

      return false;
    }

    this.internals.setValidity({});

    return true;
  }

  connectedCallback() {
    if (this.optionsSlot) {
      this.updateOptionsFromSlot(this.optionsSlot);
    }

    super.connectedCallback();

    if (!isServer) {
      this.internals.setFormValue(this.initialValue);
    }
  }

  firstUpdated() {
    setInitialSlotProperties(this, Select.slotPropertyMap);

    if (this.optionsSlot) {
      this.updateOptionsFromSlot(this.optionsSlot);
    }
  }

  focus() {
    if (this.nativeSelect) {
      this.nativeSelect.focus();
    }
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    if (this.nativeSelect) {
      this.nativeSelect.selectedIndex = 0;
    }

    this.internals.setFormValue(null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    if (state && this.nativeSelect) {
      this.nativeSelect.value = state;
      this.internals.setFormValue(state);
    } else {
      this.internals.setFormValue(null);
    }
  }

  render() {
    const descriptionId = this.description ? `${this.id}-description` : "";
    const helpId = this.help ? `${this.id}-help` : "";

    // Get the target selected value
    const selectedValue =
      this.options.length > 0 ? this.computeSelectedValue(this.options) : this.initialValue;

    return html` <div
        class=${cn("eko-select", this.class)}
        part="root"
        prefix=${ifDefined(this.prefixElement)}
        suffix=${ifDefined(this.suffixElement)}
        before=${ifDefined(this.beforeElement)}
        after=${ifDefined(this.afterElement)}>
        ${this.label
          ? html`
              <eko-label for=${this.id} ?required=${this.required} ?admin=${this.admin}
                >${this.label}</eko-label
              >
            `
          : nothing}

        <div class="eko-select__wrapper" part="wrapper">
          <slot
            name="prefix"
            @slotchange=${this.handleSlotChange}
            class="${when(this.prefixElement, () => "eko-select__prefix")}"></slot>

          <div class="eko-select__field-wrapper" part="field-wrapper">
            <slot
              name="before"
              @slotchange=${this.handleSlotChange}
              class=${when(this.beforeElement, () => "eko-select__before")}></slot>
            <div class="eko-select__field-wrapper-content" part="field-wrapper-content">
              <select
                id=${this.id}
                name=${this.name}
                ?required=${this.required}
                ?disabled=${this.disabled}
                ?readonly=${this.readonly}
                aria-describedby=${[descriptionId, helpId].filter(Boolean).join(" ")}
                class="eko-select__field"
                .value=${selectedValue}
                part="field"
                @change=${this.handleChange}
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}>
                ${when(
                  this.placeholder,
                  () => html`<option value="" disabled>${this.placeholder}</option>`
                )}
                ${this.options?.map((option) => {
                  return option.cloneNode(true) as Element;
                })}
              </select>
            </div>
            ${this.help || this.hasError
              ? html`
                  <eko-tooltip
                    position="right"
                    arrow
                    id=${helpId}
                    aria-label=${this.help || this.error}
                    class="eko-select__help"
                    part="help">
                    <eko-icon
                      name=${this.hasError ? "alert-circle" : "help-circle"}
                      theme="current"
                      size="md"></eko-icon>
                    <span slot="content">${this.help || this.error}</span>
                  </eko-tooltip>
                `
              : nothing}
            <slot
              name="after"
              slot="after"
              @slotchange=${this.handleSlotChange}
              class="eko-select__after">
              ${this.afterSlot.isEmpty
                ? html`<eko-icon name="chevron-selector-vertical"></eko-icon>`
                : nothing}
            </slot>
          </div>
          <slot
            name="suffix"
            @slotchange=${this.handleSlotChange}
            class="${when(this.suffixElement, () => "eko-select__suffix")}"></slot>
        </div>
        ${this.hasError || this.description
          ? html`<div part="hint">
              ${this.hasError
                ? html`<eko-hint error part="error">${this.error}</eko-hint>`
                : nothing}
              ${this.description
                ? html`
                    <eko-hint id=${descriptionId} part="description">
                      ${this.description}
                    </eko-hint>
                  `
                : nothing}
            </div> `
          : nothing}
      </div>
      <slot
        id="eko-select__options"
        style="display: none;"
        @slotchange=${this.handleOptionsSlotChange}></slot>`;
  }

  reportValidity(): boolean {
    return this.internals.reportValidity();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }

    if (changedProperties.has("options") && this.options.length > 0) {
      if (this.nativeSelect) {
        this.nativeSelect.value = this.computeSelectedValue(this.options);
      }

      this.updateFormValue();
      this.checkValidity();
    }
  }

  get form() {
    return this.internals.form;
  }

  private get hasError(): boolean {
    return this.error !== "";
  }

  private get initialValue(): string {
    if (this._initialValue !== null) {
      return this._initialValue;
    }

    if (isServer) {
      return "";
    }

    const lightDOMOptions = Array.from(this.querySelectorAll("option"));
    this._initialValue = this.computeSelectedValue(lightDOMOptions);

    return this._initialValue;
  }

  get validationMessage(): string {
    return this.internals.validationMessage;
  }

  get validity(): globalThis.ValidityState {
    return this.internals.validity;
  }

  get value(): string {
    if (this.nativeSelect && this.nativeSelect.options.length > 0) {
      return this.nativeSelect.value;
    }

    if (this.options.length > 0) {
      return this.computeSelectedValue(this.options);
    }

    return this.initialValue;
  }

  set value(newValue: string) {
    if (this.nativeSelect) {
      this.nativeSelect.value = newValue;
    }

    this.updateFormValue();
    this.checkValidity();
  }

  get willValidate(): boolean {
    return !this.disabled;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-select": Select;
  }
}

export default Select;
